/**
 * Single shared data source for the whole paper-usage page (both charts
 * derive from it, so their numbers agree — e.g. the Monochrome column total
 * is exactly the sum of its paper-width shares).
 *
 * One record = the print metrics of ONE printed color on ONE paper width.
 * Each widget aggregates these records along its own axis:
 * - PaperUsage (section 1) → per paper width,
 * - PrintedColorUsage (section 2) → per printed color.
 */

export const PRINTED_COLORS = [
  "Monochrome",
  "Color",
  "Front-Monochrome",
  "Back-Monochrome",
] as const;

export type PrintedColor = (typeof PRINTED_COLORS)[number];

/** Realistic paper widths (inch), ascending — section 1's category axis order. */
export const PAPER_WIDTHS = [
  8.27, 11.69, 14.96, 16.54, 18, 18.27, 19, 20.08, 22, 23.39, 24, 25.51, 26.77,
  28, 29.53, 31, 32.68, 34.25, 36, 37.4, 39.37, 41, 42.91, 44.5,
] as const;

export interface PrintColorRecord {
  width: number;
  color: PrintedColor;
  printedLength: number;
  jobCount: number;
  cLength: number;
  mLength: number;
  yLength: number;
  kLength: number;
  /** MICR (Magnetic Ink Character Recognition) length in feet. */
  micrLength: number;
}

/** Deterministic PRNG (LCG) so the fake data is stable across reloads. */
function makeRand(seed = 20260918) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

/**
 * Splits `total` into integer parts by `shares` (jittered per call), with the
 * rounding remainder folded into the largest part — parts always sum EXACTLY
 * to `total`, so aggregating back reproduces the source totals.
 */
function splitByShares(total: number, shares: number[]): number[] {
  const shareSum = shares.reduce((sum, share) => sum + share, 0);
  const parts = shares.map((share) => Math.floor((total * share) / shareSum));
  const remainder = total - parts.reduce((sum, part) => sum + part, 0);
  const largest = parts.indexOf(Math.max(...parts));
  parts[largest] += remainder;
  return parts;
}

/** Per-color split shares: [Monochrome, Color, Front-Monochrome, Back-Monochrome]. */
const LENGTH_SHARES = [0.38, 0.34, 0.16, 0.12];
const JOB_SHARES = [0.45, 0.3, 0.15, 0.1];
/** C/M/Y ink belongs to the Color rows only; K is shared by all four. */
const K_SHARES = [0.4, 0.15, 0.25, 0.2];
/** MICR ink (check/secure printing) — small volumes across all colors. */
const MICR_SHARES = [0.55, 0.2, 0.15, 0.1];

/**
 * Deterministic mock dataset (no CubeJS backend for this page): per-width
 * load profiles (bell-ish curve peaking at mid widths, printed lengths in
 * the tens of millions, job counts in the thousands) split across the four
 * printed colors. Swapping in the real API is a body-only change in the
 * widgets' `state.ts`.
 */
export function generatePrintColorRecords(): PrintColorRecord[] {
  const rand = makeRand();
  const total = PAPER_WIDTHS.length;
  const records: PrintColorRecord[] = [];

  PAPER_WIDTHS.forEach((width, index) => {
    const peak = 1 - Math.abs(index - (total - 1) / 2) / ((total - 1) / 2);
    const load = 0.15 + 0.85 * peak;

    const printedLength = Math.round(5_000_000 + load * 275_000_000 * (0.75 + rand() * 0.5));
    const jobCount = Math.round(1_500 + load * 54_000 * (0.7 + rand() * 0.6));

    const lengthParts = splitByShares(
      printedLength,
      LENGTH_SHARES.map((share) => share + rand() * 0.08),
    );
    const jobParts = splitByShares(
      jobCount,
      JOB_SHARES.map((share) => share + rand() * 0.06),
    );
    const cParts = splitByShares(Math.round(printedLength * (0.02 + rand() * 0.14)), [0, 1, 0, 0]);
    const mParts = splitByShares(Math.round(printedLength * (0.02 + rand() * 0.14)), [0, 1, 0, 0]);
    const yParts = splitByShares(Math.round(printedLength * (0.02 + rand() * 0.14)), [0, 1, 0, 0]);
    const kParts = splitByShares(
      Math.round(printedLength * (0.02 + rand() * 0.1)),
      K_SHARES.map((share) => share + rand() * 0.05),
    );
    const micrParts = splitByShares(
      Math.round(printedLength * (0.005 + rand() * 0.02)),
      MICR_SHARES.map((share) => share + rand() * 0.08),
    );

    PRINTED_COLORS.forEach((color, colorIndex) => {
      records.push({
        width,
        color,
        printedLength: lengthParts[colorIndex],
        jobCount: jobParts[colorIndex],
        cLength: cParts[colorIndex],
        mLength: mParts[colorIndex],
        yLength: yParts[colorIndex],
        kLength: kParts[colorIndex],
        micrLength: micrParts[colorIndex],
      });
    });
  });

  return records;
}
